import React from 'react';
import { reaction, IReactionDisposer } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import Store from '../../../store';
import { Alert, Card, Col, Row } from 'react-bootstrap';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import Spinner from '../../components/Spinner';
import LoadedDatasetOverview from '../../components/overview/LoadedDatasetOverview';
import Error from '../../components/error/Error';
import BadDatasetMessage from '../../components/overview/BadDatasetMessage';
import NoDatasetCard from '../../components/overview/NoDatasetCard';
import { ModelChatSlot } from '../../components/model/heroHost';
import { ModelSetupLinks } from '../../components/model/ModelSetupLinks';
import { ModelNotesPanel } from '../../components/model/ModelNotesPanel';
import './modelFacts.css';

type MatchParams = {
  tenant: string;
  modelId: string;
};

// The dates and lineage that used to fill half the page. They are reference facts,
// not things anyone reads on the way in, so they get one compact strip at the top
// of the metadata column.
function ModelFacts({
  createdAt,
  updatedAt,
  lastDeployedAt,
  live,
  duplicateOf,
  duplicateOfName,
  tenant,
}: {
  createdAt: string;
  updatedAt: string;
  lastDeployedAt?: string;
  live: boolean;
  duplicateOf?: number;
  duplicateOfName?: string;
  tenant: string;
}) {
  // One line, not a row of labelled columns.
  //
  // These are reference facts, which is what this card has always called them: nobody
  // opens a model to read when it was created. Two uppercase headings over two dates
  // took three lines of card, and the second is dropped when it repeats the first,
  // which on a model nobody has edited since creating it is most of the time. The whole
  // line sits opposite the admin chips rather than above them behind a rule.
  //
  // A DEPLOY date is never dropped, even when it matches the creation date: that one is
  // a different kind of fact and its absence would read as never deployed.
  // A live model shows when it was last DEPLOYED, anything else shows when it was last
  // edited. Two different facts that were sharing one label, so a deploy date read as
  // an edit.
  //
  // A live model with no lastDeployedAt falls back to the edit date rather than calling
  // it a deployment: `moment(undefined)` is NOW, so the alternative is a confident
  // "Deployed today" on a model that may never have deployed at all.
  const deployed = live && !!lastDeployedAt;
  const created = moment(createdAt).format('MMM DD, YYYY');
  const deployedOn = deployed
    ? moment(lastDeployedAt).format('MMM DD, YYYY')
    : null;
  const updatedOn = moment(updatedAt).format('MMM DD, YYYY');
  const second = deployedOn
    ? `Deployed ${deployedOn}`
    : updatedOn === created
    ? null
    : `Updated ${updatedOn}`;

  return (
    <Card border="light">
      <Card.Body className="mfacts py-3">
        <div className="mfacts-dates">
          <span>Created {created}</span>
          {second && <span className="mfacts-sep">{second}</span>}
          {/* Absent on models built from scratch, where the old page drew an empty
              "Duplicate of model #" link.

              By name, with the id as the fallback rather than the other way round: it
              rendered `#3 (E2E Stage Test Customer Fit Model)`, which leads with an
              internal identifier and brackets the only part a reader recognises. The
              name is missing when that model is not in the store's list, which is why
              the id is still what the link falls back to. */}
          {duplicateOf ? (
            <span className="mfacts-sep">
              Copy of{' '}
              <a
                className="mfact-link"
                href={`/tenant/${tenant}/models/${duplicateOf}`}
              >
                {duplicateOfName || `model #${duplicateOf}`}
              </a>
            </span>
          ) : null}
        </div>
        {/* The deterministic half of what the generated summary used to carry: three
            links to the screens that own this model's setup. Opposite the dates rather
            than under a rule, because a card holding one line of text and three chips
            does not need to be three strips tall. */}
        <ModelSetupLinks tenant={tenant} />
      </Card.Body>
    </Card>
  );
}

// Reference below the conversation: the model's own facts on the left, what has been
// written about it on the right.
//
// Two cards have left this row. The generated model summary went first: it answered one
// question, always the same one, whether or not it was the one being asked. Then the
// "Ask about this model" card, which listed three questions and handed each to the rail
// to answer, so the page drew the questions in one place and the answers in another and
// on a wide screen drew them twice. Both are now the one surface above this row, which
// asks AND answers.
function OverviewColumns({
  metadata,
  secondary,
}: {
  metadata: React.ReactNode;
  secondary: React.ReactNode;
}) {
  return (
    <Row className="g-3 cq-overview">
      <Col lg={7}>
        <div className="d-flex flex-column gap-3">{metadata}</div>
      </Col>
      <Col lg={5}>
        <div className="d-flex flex-column gap-3">{secondary}</div>
      </Col>
    </Row>
  );
}

interface OverviewProps extends RouteComponentProps<MatchParams> {
  store?: Store;
}

const Overview = inject('store')(
  observer(
    class Overview extends React.Component<
      OverviewProps,
      Record<string, never>
    > {
      interval: number;
      disposeLoadingReaction?: IReactionDisposer;

      constructor(props: OverviewProps) {
        super(props);
        this.cleanCache = this.cleanCache.bind(this);
        this.loadData = this.loadData.bind(this);
      }

      async componentDidMount() {
        const { rootPage } = this.props.store;

        // If dataset is loading, no need to load data
        if (
          !rootPage.isDataSetLoadingProgress &&
          rootPage.duplicationStatus === 'succeeded'
        ) {
          await this.loadData();
          clearInterval(this.interval);
        } else {
          this.startPolling();
        }

        // A reload/recompute triggered while already on this page flips
        // isDataSetLoadingProgress to true without remounting, so the mount-time
        // check above misses it and no polling ever starts. React to the flag
        // so polling begins whenever a load starts, not only when the page is
        // (re)opened mid-load. getDataSetLoadingProgress reloads the page when
        // the load finishes, so stopping is handled by unmount.
        this.disposeLoadingReaction = reaction(
          () => this.props.store.rootPage.isDataSetLoadingProgress,
          (loading) => {
            if (loading) {
              this.startPolling();
            }
          }
        );
      }

      UNSAFE_componentWillUnmount() {
        clearInterval(this.interval);
        this.disposeLoadingReaction?.();
      }

      startPolling() {
        if (this.interval) {
          return;
        }
        const { tenant, modelId } = this.props.store;
        this.interval = window.setInterval(async () => {
          await Promise.all([
            this.props.store.rootPage.getDataSetLoadingProgress({
              tenant,
              modelId,
            }),
            this.props.store.rootPage.getDuplicationStatus({
              tenant,
              modelId,
            }),
          ]);
        }, 10000);
      }

      async cleanCache() {
        await this.props.store.cleanCache();
      }

      async loadData() {
        const { tenant, modelId } = this.props.store;
        await this.props.store.dataPage.get(tenant, modelId);
      }

      render() {
        const { csvPage, rootPage, activeModel, dataPage } = this.props.store;
        const { dataSets } = csvPage;
        const { dataSetMetadata } = dataSets;

        const {
          cleaningCache,
          isDataSetLoadingProgress,
          isDataSetLoadingError,
          duplicationStatus,
        } = rootPage;
        const {
          type: modelType,
          live,
          lastDeployedAt,
          updatedAt,
          createdAt,
          duplicateOf,
        } = activeModel;
        const { tenant, modelId } = this.props.match.params;

        const duplicateOfName = this.props.store.tenantModels.find(
          ({ modelId }) => modelId === duplicateOf
        )?.name;

        return (
          <div className="mt-4">
            {dataSets.uploadDataSetError && (
              <Error
                message={dataSets.uploadDataSetError.message}
                type={dataSets.uploadDataSetError.type}
              />
            )}
            <BadDatasetMessage />
            {isDataSetLoadingProgress ? (
              <Card
                className="justify-content-center d-flex align-items-center"
                style={{ height: '30rem' }}
              >
                <Spinner />
                Dataset loading in process, it can take a few minutes to a few
                hours. You&#180;ll receive an email when finished. Feel free to
                take a break, or browse{' '}
                <a
                  href="https://help.madkudu.com/docs/introduction-to-madkudu-data-studio"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  our documentation
                </a>{' '}
                to know what to expect next.
              </Card>
            ) : duplicationStatus === 'in_progress' ? (
              <Card
                className="justify-content-center d-flex align-items-center"
                style={{ height: '30rem' }}
              >
                <Spinner />
                Model duplication in progress, it can take a few minutes to.
                You&#180;ll receive an email when finished. Feel free to take a
                break, or browse{' '}
                <a
                  href="https://help.madkudu.com/docs/introduction-to-madkudu-data-studio"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  our documentation
                </a>{' '}
                to know what to expect next.
              </Card>
            ) : (
              <>
                {isDataSetLoadingError && (
                  <Alert variant="danger">
                    The last dataset loading attempt has failed. The dataset
                    currently loaded is the previous one.
                  </Alert>
                )}
                {/* The conversation, first and full width. `ModelChatSlot` is where
                    the model's chat renders while this tab is open (see heroHost):
                    the same panel that is the rail everywhere else, so leaving this
                    tab carries the conversation into the rail rather than ending it.

                    Customer fit only, and this is a limit of the AGENT rather than of
                    the slot. Every Studio agent is built for fit models: measured on
                    2026-09-22, `inbound-model-builder` mentions trees and splits 150
                    times against 5 engagement terms, and `model-summary`,
                    `model-execution`, `model-personas` and `tree-diagnostics` mention
                    engagement zero times between them. The rail already offers that
                    agent on every model type and has done for weeks; what this slot
                    changes is prominence, from a column a reader can ignore to the
                    first thing on the tab at full width. That is worth having on the
                    models it can answer for and not on the ones it cannot.

                    Engagement models are getting their own surface rather than a
                    better answer here: `exp/engagement-model` routes them to
                    EngagementWorkspace instead of this page entirely, behind
                    `studio-engagement-workspace`. So this condition covers the window
                    where that flag is off, and stops mattering once it is on.

                    Remove this when an engagement agent exists. It is one condition. */}
                {modelType === 'customer_fit' && <ModelChatSlot />}
                <OverviewColumns
                  metadata={
                    <>
                      {/* Both dates go in; ModelFacts picks and labels one, so
                          the value and its label cannot come apart. */}
                      <ModelFacts
                        tenant={tenant}
                        createdAt={createdAt}
                        updatedAt={updatedAt}
                        lastDeployedAt={lastDeployedAt}
                        live={live}
                        duplicateOf={duplicateOf}
                        duplicateOfName={duplicateOfName}
                      />
                      {/* The dataset block's empty state, in the dataset block's
                          place. It spanned the full width below both columns, which
                          put it under the notes card and left the column it belongs
                          to ending early. */}
                      {!dataSetMetadata &&
                        !isDataSetLoadingProgress &&
                        duplicationStatus === 'succeeded' && (
                          <NoDatasetCard
                            tenant={tenant}
                            modelId={modelId}
                            modelType={modelType}
                          />
                        )}
                      {dataPage.overviews && (
                        <LoadedDatasetOverview
                          tenant={tenant}
                          modelId={modelId}
                          overviews={dataPage.overviews}
                          cleanCache={this.cleanCache}
                          cleaningCache={cleaningCache}
                        />
                      )}
                    </>
                  }
                  secondary={<ModelNotesPanel />}
                />
              </>
            )}
          </div>
        );
      }
    }
  )
);

export default withRouter(Overview);
